<template>
  <div>
    <div class="md-layout md-gutter">
      <!-- forest ty -->
      <div class="md-layout-item md-size-100">
        <md-field>
          <std-autocomplete
            v-if="loadStd3"
            :data="std3List"
            :label="$t('typeInfo.forestType')"
            :initialValue="typeInfo.forest_type_std3"
            :initialDisplay="initForestType"
            @result="getForestType"></std-autocomplete>
        </md-field>
      </div>

      <!-- species -->
      <div class="md-layout-item md-size-100">
        <md-field>
          <std-autocomplete
            v-if="loadStd2"
            :data="std2List"
            :label="$t('typeInfo.woodSpecies')"
            :initialValue="typeInfo.forest_species_std2"
            :initialDisplay="initSpecie"
            @result="getSpecie"></std-autocomplete>
        </md-field>
      </div>

      <!-- manag system -->
      <div class="md-layout-item md-size-100">
        <md-field>
          <select v-model="typeInfo.management_system" name="managSys">
            <option disabled value="">{{ $t("typeInfo.managSys") }}</option>
            <option v-for="(value, key) in managSysList" :value="key" :key="key">{{ value }}</option>
          </select>
        </md-field>
      </div>

      <!-- age class -->
      <div class="md-layout-item md-size-50">
        <md-field v-show="ac1">
          <select v-model="typeInfo.age_class1" name="ageClass1">
            <option disabled value="">{{ $t("typeInfo.ageClass") }}</option>
            <option v-for="(age1, key) in ageClass1List" :value="key" :key="key">{{ age1 }}</option>
          </select>
        </md-field>
      </div>
      <div class="md-layout-item md-size-50">
        <md-field v-show="ac2">
          <select v-model="typeInfo.age_class2" name="ageClass2">
            <option disabled value="">{{ $t("typeInfo.ageClass") }}</option>
            <option v-for="(age2, key) in ageClass2List" :value="key" :key="key">{{ age2 }}</option>
          </select>
        </md-field>
      </div>

      <div class="md-layout-item md-size-50">
        <toggle-buttons :btnList="crownDensityList" :label="$t('typeInfo.crownDensity')" @btnValue="getCrownDensity" :editTB="typeInfo.crown_density" />
      </div>
      <div class="md-layout-item md-size-50">
        <toggle-buttons :btnList="verticalStructList" :label="$t('typeInfo.verticalStruct')" @btnValue="getVerticalStruct" :editTB="typeInfo.vertical_structure" />
      </div>
    </div>

    <!-- table -->
    <div class="md-layout md-gutter forest-table">
      <div class="md-layout-item md-size-100">
        <b-tabs>
          <b-tab title="25 m²" active>
            <forest-table :slope="slope" :area="25" @forestTable="getForestTable" :editTable="editTable[25]" />
          </b-tab>
          <b-tab title="200 m²">
            <forest-table :slope="slope" :area="200" @forestTable="getForestTable" :editTable="editTable[200]" />
          </b-tab>
          <b-tab title="400 m²">
            <forest-table :slope="slope" :area="400" @forestTable="getForestTable" :editTable="editTable[400]" />
          </b-tab>
        </b-tabs>
      </div>
    </div>

    <!-- add non wood -->
    <div class="md-layout md-gutter md-alignment-center-center">
      <md-button class="md-raised md-primary" @click="addNw()">{{ btnAddNw }}</md-button>
    </div>

    <error-dialog :show="error" :text="$t('error.dataCheck')" @closeError="error = false" />
  </div>
</template>

<script>
import ToggleButtons from '@/components/ToggleButtons'
import ForestTable from '@/components/plotType/ForestTable'
import StdAutocomplete from '@/components/StdAutocomplete'
import ErrorDialog from '@/components/ErrorDialog'

import EventBus from '@/event-bus'
import { getDomain } from '@/assets/scripts/domainsManager'
import { formatList, getValue, checkDist, checkAzimuth, checkRange, normalize } from '@/assets/scripts/formatData'

const AREA = 'plot_radius'
const STD3 = 'std3'
const STD2 = 'std2'
const MANAG_SYS = 'management_system'
const AGE_CLASS = 'age_class'
const CROWN_DENSITY = 'crown_density'
const VERTICAL_STRUCTURE = 'vertical_structure'

export default {
  props: ['slope'],
  components: {
    ToggleButtons,
    ForestTable,
    StdAutocomplete,
    ErrorDialog
  },
  data: () => ({
    typeInfo: {
      plot_radius: {
        25: 0,
        200: 0,
        400: 0
      },
      forest_type_std3: '',
      forest_species_std2: '',
      management_system: '',
      age_class1: '',
      age_class2: '',
      crown_density: '',
      vertical_structure: '',
      forestTable: {
        25: [],
        200: [],
        400: []
      }
    },
    std2List: {},
    std3List: {},
    managSysList: {},
    ageClass1List: [],
    ageClass2List: [],
    crownDensityList: {},
    verticalStructList: {},
    ac1: false,
    ac2: false,
    editTable: {},
    btnAddNw: '',
    edit: false,
    loadStd3: false,
    loadStd2: false,
    initForestType: '',
    initSpecie: '',
    error: false
  }),
  mounted () {
    this.$store.state.plot.id === null ? this.edit = false : this.edit = true
    this.setLists()

    if (this.edit) {
      this.typeInfo = this.$store.state.plot.typeInfo // ref to store
      this.editTable = this.$store.state.plot.typeInfo.forestTable
    }

    if (this.edit && this.$store.state.plot.typeInfo.nonwood !== undefined) { // existing nonwood to edit
      this.btnAddNw = this.$i18n.t('global.edit') + ' ' + this.$i18n.t('plot.plotTypeList.nonwood')
    } else {
      this.btnAddNw = this.$i18n.t('global.add') + ' ' + this.$i18n.t('plot.plotTypeList.nonwood')
    }

    EventBus.$on('save', () => {
      this.saveData()
    })
  },
  computed: {
    // added in order to watch the typeInfo.managSys changing
    managSys () {
      return this.typeInfo.management_system
    }
  },
  watch: {
    managSys () {
      let key = this.typeInfo.management_system
      getDomain(AGE_CLASS).then(data => {
        let ac = data[this.$i18n.locale]
        if (key === '01' || key === '02' || key === '03') {
          this.ac1 = true
          this.ac2 = false
          this.ageClass1List = ac[key]
        } else if (key === '06') {
          this.ac1 = true
          this.ac2 = true
          this.ageClass1List = ac['01']
          this.ageClass2List = ac['02']
        } else if (key === '07') {
          this.ac1 = true
          this.ac2 = true
          this.ageClass1List = ac['02']
          this.ageClass2List = ac['01']
        } else {
          this.ac1 = false
          this.ac2 = false
        }
      })
    }
  },
  methods: {
    saveData: function () {
      if (this.checkData()) {
        this.normalizeTable(25)
        this.normalizeTable(200)
        this.normalizeTable(400)
        // send data only in new
        this.edit ? this.$emit('typeInfo', null) : this.$emit('typeInfo', this.typeInfo)
      } else {
        this.error = true
      }
    },

    setLists: function () {
      getDomain(AREA).then(data => {
        this.typeInfo.plot_radius[25] = data[this.slope][25]
        this.typeInfo.plot_radius[200] = data[this.slope][200]
        this.typeInfo.plot_radius[400] = data[this.slope][400]
      })
      getDomain(STD2).then(data => {
        this.std2List = formatList(data[this.$i18n.locale])
        if (this.edit) {
          this.initSpecie = getValue(this.std2List, this.typeInfo.forest_species_std2)
        }
        this.loadStd2 = true
      })
      getDomain(STD3).then(data => {
        this.std3List = formatList(data[this.$i18n.locale])
        if (this.edit) {
          this.initForestType = getValue(this.std3List, this.typeInfo.forest_type_std3)
        }
        this.loadStd3 = true
      })
      getDomain(MANAG_SYS).then(data => {
        this.managSysList = data[this.$i18n.locale]
      })
      getDomain(VERTICAL_STRUCTURE).then(data => {
        this.verticalStructList = data[this.$i18n.locale]
      })
      getDomain(CROWN_DENSITY).then(data => {
        this.crownDensityList = data[this.$i18n.locale]
      })
    },

    addNw: function () {
      // send the tmp info to the parent only in new
      this.edit ? this.$emit('tmpInfo', null) : this.$emit('tmpInfo', this.typeInfo)
    },

    getCrownDensity: function (value) {
      this.typeInfo.crown_density = value
    },

    getVerticalStruct: function (value) {
      this.typeInfo.vertical_structure = value
    },

    getForestTable: function (value) {
      this.typeInfo.forestTable[value.area] = value.forest
    },

    getSpecie: function (specie) {
      this.typeInfo.forest_species_std2 = specie
    },

    getForestType: function (forestType) {
      this.typeInfo.forest_type_std3 = forestType
    },

    checkData: function () {
      let check = true
      if (!this.checkTable(25)) check = false
      if (!this.checkTable(200)) check = false
      if (!this.checkTable(400)) check = false

      return check
    },

    checkTable: function (table) {
      let check = true
      if (this.typeInfo.forestTable[table].length > 0) {
        this.typeInfo.forestTable[table].forEach(forest => {
          if (!checkRange(forest.number, 0, 999)) check = false
          if (!checkDist(forest.dist)) check = false
          if (!checkAzimuth(forest.azimuth)) check = false
        })
        return check
      } else {
        return check
      }
    },

    normalizeTable: function (table) {
      this.typeInfo.forestTable[table].forEach(item => {
        if (item.number !== null) item.number = normalize(item.number, 0)
        if (item.dist !== null) item.dist = normalize(item.dist, 2)
        if (item.azimuth !== null) item.azimuth = normalize(item.azimuth, 0)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/style/arcadia_theme.scss';

  .forest-table {
    margin-bottom: 24px;
    margin-top: 20px;
  }
</style>
